import './paginated-table.scss';

import {
  SortOrder,
  SubmissionFieldIdType,
  SubmissionSortKeys,
} from '@converge/data';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { config } from '@converge/config';
import { useState } from 'react';

export interface ITableColumn<T, PR> {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center' | 'justify' | undefined;
  headerAlign?: 'left' | 'right' | 'center' | 'justify' | undefined;
  width?: string;
  render: (
    row: T,
    index: number,
    parent?: PR
  ) => JSX.Element | string | number | undefined;
  rowSpan?: (row: T) => number;
  skipRowSpanColumn?: (row: T) => boolean;
  isSortable?: boolean;
  visible?: boolean;
}

export interface ISortProps {
  id: string;
  order: SortOrder;
}

interface ITableProps<R, PR, CR> {
  columns: ITableColumn<R, PR>[];
  rows: R[] | undefined;
  emptyRowMessage?: string | null;
  expandable?: boolean;
  childColumns?: ITableColumn<CR, R>[];
  getChildRows?: (row: R) => CR[];
  parentRow?: PR;
}

type PaginationProps =
  | {
      enablePagination: true;
      totalRows: number;
      rowsPerPage: number;
      currentPage: number;
      handleChangeRowsPerPage: (newRowsPerPage: number) => void;
      handleChangePage: (newPage: number) => void;
    }
  | {
      enablePagination?: false;
      totalRows?: never;
      rowsPerPage?: never;
      currentPage?: never;
      handleChangeRowsPerPage?: never;
      handleChangePage?: never;
    };

type SortingProps =
  | {
      handleSort: (sort: ISortProps) => void;
      currentSort: ISortProps;
    }
  | {
      handleSort?: never;
      currentSort?: never;
    };

export type PaginatedTableProps<R, PR, CR> = ITableProps<R, PR, CR> &
  PaginationProps &
  SortingProps & {
    customSort?: boolean;
  };

export const PaginatedTable = <R, PR = void, CR = void>({
  columns,
  rows,
  emptyRowMessage,
  expandable,
  childColumns,
  parentRow,
  enablePagination,
  totalRows,
  rowsPerPage,
  currentPage,
  getChildRows = () => [],
  handleChangeRowsPerPage,
  handleChangePage,
  handleSort,
  currentSort,
  customSort,
}: PaginatedTableProps<R, PR, CR>) => {
  const filteredColumns = columns.filter((column) => column.visible ?? true);

  return (
    <Table className="paginated-table">
      <TableHead>
        <TableRow>
          {filteredColumns.map((column) =>
            column.isSortable ?? false ? (
              <RenderSortableHeaderCell
                column={column}
                customSort={customSort}
                currentSort={currentSort}
                handleSort={(sort: ISortProps) => handleSort?.(sort)}
                key={column.id}
              />
            ) : (
              <RenderHeaderCell {...column} key={column.id} />
            )
          )}
          {childColumns && expandable && <TableCell />}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows?.map((row, index) =>
          expandable ? (
            <CollapsiblePaginatedTableRow
              row={row}
              columns={filteredColumns}
              getChildRows={getChildRows}
              childColumns={childColumns}
              parentRow={parentRow}
              key={index}
              index={index}
            />
          ) : (
            <PaginatedTableRow
              row={row}
              parentRow={parentRow}
              columns={filteredColumns}
              key={index}
              index={index}
            />
          )
        )}
        {!rows?.length && (
          <TableRow>
            <TableCell colSpan={filteredColumns.length + (expandable ? 1 : 0)}>
              {emptyRowMessage ?? 'No data found'}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {enablePagination && (
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={config.paginatedTableRowsPerPageOptions}
              count={totalRows}
              rowsPerPage={rowsPerPage}
              page={currentPage}
              onPageChange={(_event, newPage) => handleChangePage(newPage)}
              onRowsPerPageChange={(event) =>
                handleChangeRowsPerPage(+event.target.value)
              }
            />
          </TableRow>
        </TableFooter>
      )}
    </Table>
  );
};

interface ICollapsiblePaginatedTableRowProps<R, PR, CR> {
  row: R;
  parentRow?: PR;
  columns: ITableColumn<R, PR>[];
  childColumns?: ITableColumn<CR, R>[];
  getChildRows: (row: R) => CR[];
  index: number;
}

const CollapsiblePaginatedTableRow = <T, PR, CR>({
  row,
  columns,
  parentRow,
  getChildRows,
  childColumns,
  index,
}: ICollapsiblePaginatedTableRowProps<T, PR, CR>) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ background: '#ffffff' }}>
        {columns.map((column) =>
          column.rowSpan &&
          column.skipRowSpanColumn &&
          column.skipRowSpanColumn(row) ? null : (
            <TableCell
              key={column.id}
              align={column.align}
              width={column.width}
              rowSpan={column.rowSpan && column.rowSpan(row)}
            >
              {column.render(row, index, parentRow)}
            </TableCell>
          )
        )}
        {childColumns && (
          <TableCell align="right">
            <IconButton
              aria-label="expand row"
              size="small"
              data-testid="expand-row-button"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      {childColumns && (
        <TableRow>
          <TableCell className="expanded-row" colSpan={columns.length + 1}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Paper className="expanded-row-paper" elevation={0}>
                <PaginatedTable
                  columns={childColumns}
                  rows={getChildRows(row)}
                  parentRow={row}
                  enablePagination={false}
                />
              </Paper>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface IPaginatedTableRowProps<T, PR> {
  row: T;
  parentRow?: PR;
  columns: ITableColumn<T, PR>[];
  index: number;
}

const PaginatedTableRow = <T, PR>({
  row,
  parentRow,
  columns,
  index,
}: IPaginatedTableRowProps<T, PR>) => {
  return (
    <TableRow className="paginated-table-row-container">
      {columns.map((column) =>
        column.rowSpan &&
        column.skipRowSpanColumn &&
        column.skipRowSpanColumn(row) ? null : (
          <TableCell
            key={column.id}
            align={column.align}
            width={column.width}
            rowSpan={column.rowSpan && column.rowSpan(row)}
          >
            {column.render(row, index, parentRow)}
          </TableCell>
        )
      )}
    </TableRow>
  );
};

const RenderHeaderCell = <T, PR>(column: ITableColumn<T, PR>) => {
  return (
    <TableCell
      className="column-label"
      key={column.id}
      align={column.headerAlign}
    >
      {column.label}
    </TableCell>
  );
};

const RenderSortableHeaderCell = <T, PR>(props: {
  column: ITableColumn<T, PR>;
  currentSort?: ISortProps;
  customSort?: boolean;
  handleSort: (sort: ISortProps) => void;
}) => {
  const { column, currentSort, handleSort, customSort } = props;
  const getSubmissionSortKey = (columnId: string) => {
    switch (columnId) {
      case SubmissionFieldIdType.SubmissionReferenceId:
        return SubmissionSortKeys.ReferenceId;
      case SubmissionFieldIdType.Brokerage:
        return SubmissionSortKeys.AgencyName;
      case SubmissionFieldIdType.Broker:
        return SubmissionSortKeys.AgentFirstName;
      case SubmissionFieldIdType.Status:
        return SubmissionSortKeys.Status;
      case SubmissionFieldIdType.PolicyType:
        return SubmissionSortKeys.Type;
      case SubmissionFieldIdType.ReceivedAt:
        return SubmissionSortKeys.ReceivedAt;
      case SubmissionFieldIdType.InsuredName:
        return SubmissionSortKeys.InsuredName;
      case SubmissionFieldIdType.UnderWriter:
        return SubmissionSortKeys.AssigneeUsername;
      case SubmissionFieldIdType.DigitalSource:
        return SubmissionSortKeys.DigitalSource;
      case SubmissionFieldIdType.PolicyStartDate:
        return SubmissionSortKeys.PolicyStartDate;
      default:
        return SubmissionSortKeys.DueDate;
    }
  };
  const [sortSelected, setSortSelected] = useState('');
  const getCurrentSortId = (columnId: string) => {
    const sortMap: { [key: string]: SubmissionSortKeys } = {
      [SubmissionFieldIdType.SubmissionReferenceId]:
        SubmissionSortKeys.ReferenceId,
      [SubmissionFieldIdType.Brokerage]: SubmissionSortKeys.AgencyName,
      [SubmissionFieldIdType.Broker]: SubmissionSortKeys.AgentFirstName,
      [SubmissionFieldIdType.Status]: SubmissionSortKeys.Status,
      [SubmissionFieldIdType.PolicyType]: SubmissionSortKeys.Type,
      [SubmissionFieldIdType.ReceivedAt]: SubmissionSortKeys.ReceivedAt,
      [SubmissionFieldIdType.InsuredName]: SubmissionSortKeys.InsuredName,
      [SubmissionFieldIdType.UnderWriter]: SubmissionSortKeys.AssigneeUsername,
      [SubmissionFieldIdType.DueDate]: SubmissionSortKeys.DueDate,
      [SubmissionFieldIdType.PolicyStartDate]:
        SubmissionSortKeys.PolicyStartDate,
    };
    return sortMap[columnId];
  };
  return (
    <TableCell
      key={column.id}
      align={column.headerAlign}
      sortDirection={currentSort?.order === SortOrder.Asc ? 'asc' : 'desc'}
    >
      <TableSortLabel
        active={currentSort?.id === getCurrentSortId(column.id)}
        direction={
          currentSort?.id === getCurrentSortId(column.id)
            ? currentSort?.order === SortOrder.Asc
              ? 'asc'
              : 'desc'
            : 'asc'
        }
        IconComponent={(props) => (
          <span className="sort-icon">
            <ArrowDropUpIcon
              viewBox="0 -8 24 24"
              onClick={() => setSortSelected(SortOrder.Asc)}
              color={
                currentSort?.id === getCurrentSortId(column.id) &&
                currentSort?.order !== sortSelected &&
                currentSort?.order === SortOrder.Asc
                  ? 'primary'
                  : 'disabled'
              }
            />
            <ArrowDropDownIcon
              viewBox="0 8 24 24"
              onClick={() => setSortSelected(SortOrder.Desc)}
              color={
                currentSort?.id === getCurrentSortId(column.id) &&
                currentSort?.order !== sortSelected &&
                currentSort?.order === SortOrder.Desc
                  ? 'primary'
                  : 'disabled'
              }
            />
          </span>
        )}
        onClick={() => {
          handleSort({
            id: customSort ? getSubmissionSortKey(column.id) : column.id,
            order:
              currentSort?.id === getSubmissionSortKey(column.id) ||
              currentSort?.id === column.id
                ? currentSort?.order === SortOrder.Asc &&
                  currentSort?.order !== sortSelected
                  ? SortOrder.Desc
                  : SortOrder.Asc
                : SortOrder.Asc,
          });
        }}
      >
        {column.label}
      </TableSortLabel>
    </TableCell>
  );
};
